import { Select } from "antd";
import { getProject, getProjects } from "api/project.api";
import { getRolesData, getTalentPoolRolesData } from "api/roles.api";
import { SvgIcons } from "Icon/SvgIcons";
import React, { useEffect, useState } from "react";

interface SelectTalentPoolProps {
    handleSelectChange: (value: number) => void;
    selectedPool?: number | null;
    isAddToProject?: boolean;
}

export interface TalentPool {
    id: number;
    owner: string;
}

const getSelectLabel = (isAddToProject?: boolean) => {
    if (isAddToProject) return "Project";
    return "Talent Pool";
};

const SelectTalentPool: React.FC<SelectTalentPoolProps> = ({ handleSelectChange, selectedPool, isAddToProject }) => {
    const [talentPools, setTalentPools] = useState<TalentPool[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);

    const refreshTalentPools = async () => {
        setLoading(true);
        try {
            const data = await getProjects(1, !isAddToProject);
            setTotalPages(data?.total_pages);
            const formattedData = data?.results.map((pool) => ({
                id: pool.id,
                owner: pool.title,
            }));
            setTalentPools(formattedData);
            setPage(2);
        } catch (error) {
            console.error("Error fetching talent pools:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTalentPools = async () => {
        if (loading || page > totalPages) return;
        setLoading(true);
        try {
            const data = await getProjects(page);
            const formattedData = data?.results.map((pool) => ({
                id: pool.id,
                owner: pool.title,
            }));
            setTalentPools((prev) => [...prev, ...formattedData]);
            setPage((prev) => prev + 1);
        } catch (error) {
            console.error("Error fetching talent pools:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTalentPools = async () => {
        setLoading(true);
        try {
            const data = isAddToProject ? await getRolesData() : await getTalentPoolRolesData();
            const formattedData = data.map((pool) => ({
                id: pool.id,
                owner: pool.title,
            }));
            setTalentPools((prev) => [...prev, ...formattedData]);
            setPage((prev) => prev + 1);
        } catch (error) {
            console.error("Error fetching talent pools:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // refreshTalentPools();
        fetchAllTalentPools();
    }, []);

    const selectLabel = getSelectLabel(isAddToProject);

    return (
        <div className="flex flex-col gap-[6px] mt-4">
            <span className="text-text-secondary font-medium text-sm flex items-center gap-1">
                Select {selectLabel} <span className="text-primary"> *</span>
                {SvgIcons.questionSvg}
            </span>
            <div className="relative">
                <span className="absolute top-1/2 left-2 -translate-y-1/2">
                    {SvgIcons.threeLayersSvg}
                </span>
                <Select
                    placeholder={`Select ${selectLabel} Name`}
                    optionFilterProp="children"
                    className="border rounded-md border-main-border"
                    variant="borderless"
                    showSearch
                    filterOption={(input, option) =>
                        typeof option?.label === "string" && option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{
                        marginInlineEnd: 4,
                        color: "#344054",
                        display: "flex",
                        alignItems: "center",
                        textTransform: "capitalize",
                        paddingLeft: "18px",
                        gap: "5px",
                        fontWeight: 500,
                    }}
                    options={talentPools
                        .filter(pool => pool.id !== selectedPool)
                        .map(pool => ({
                            label: pool.owner,
                            value: pool.id,
                        }))}
                    virtual
                    loading={loading}
                    onChange={(value) => handleSelectChange(value)} // Call parent function when value changes
                />
            </div>
        </div>
    );
};

export default SelectTalentPool;
